/**
 * Engagement plugin: tracks active time while the tab is visible.
 */
import type { PluginContext } from "../lib/types";
import { now } from "../lib/utils";

import { registerPlugin } from "./register";

export function createEngagementPlugin(ctx: PluginContext) {
  let engagedMs = 0;
  let reportedMs = 0;
  let lastActiveAt = now();
  let active = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  const tick = (final = false) => {
    if (!active) return;
    const current = now();
    const elapsed = Math.max(0, current - lastActiveAt);
    lastActiveAt = current;
    if (!ctx.canTrackCategory("behavioral_events")) return;
    engagedMs += elapsed;
    const roundedMs = Math.round(engagedMs);
    if (roundedMs <= reportedMs) return;
    ctx.track("engagement", {
      engaged_ms: roundedMs,
      final,
      url: window.location.href,
    });
    reportedMs = roundedMs;
  };

  const stopHeartbeat = () => {
    if (timer === undefined) return;
    clearInterval(timer);
    timer = undefined;
  };

  const startHeartbeat = () => {
    if (!active || timer !== undefined) return;
    timer = setInterval(() => tick(false), ctx.config.engagement.heartbeatMs);
  };

  const finishVisibleSlice = () => {
    tick(true);
    active = false;
    stopHeartbeat();
  };

  const handleVisibility = () => {
    const visible = document.visibilityState === "visible";
    if (visible === active) return;
    if (!visible) {
      // Finish the visible slice now; a background timer must not keep
      // replaying the same total or extending the visitor's activity.
      finishVisibleSlice();
    } else {
      active = true;
      lastActiveAt = now();
      startHeartbeat();
    }
  };

  return {
    onInit() {
      active = document.visibilityState === "visible";
      lastActiveAt = now();
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("pageshow", handleVisibility);
      startHeartbeat();
    },
    onPageHide: finishVisibleSlice,
    onUnload() {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handleVisibility);
      finishVisibleSlice();
    },
  };
}

registerPlugin("engagement", createEngagementPlugin);
